import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { can, api, hasRole } from "@/lib/api";
import { DesignFloatBar } from "@/components/erp/DesignFloatBar";
import { SpecialPickModal } from "@/components/erp/DesignExtras";
import { IdleWatch } from "@/components/erp/IdleWatch";
import { ProdTimerWatcher } from "@/components/erp/prod/ProdTimerWatcher";
import { NotificationBell } from "@/components/erp/NotificationBell";
import { MenuOrderModal, applyOrder } from "@/components/erp/MenuOrder";
import { ScoreStrip } from "@/components/erp/ScoreStrip";
import { ChangePasswordModal } from "@/components/erp/ChangePassword";
import {
  Award, Wrench,  LayoutDashboard, Users, FileText, FolderKanban, ClipboardList, Factory, Hammer,
  Boxes, ShoppingCart, UserCog, CalendarCheck, Clock, Wallet, Landmark, PiggyBank,
  Settings, ScrollText, LogOut, Menu, X, Monitor, Store, Workflow, PackageSearch, Building2, Truck, Calculator, ReceiptText, ListTodo, PackageCheck, TrendingDown, BookOpen, HardDrive, PenTool, CalendarDays, ArrowUpDown, Target, KeyRound
} from "lucide-react";

export const MENU = [
  { to: "/app/dashboard", icon: LayoutDashboard, label: "Dashboard", mod: "dashboard" },
  { to: "/app/rapor", icon: Award, label: "Rapor Saya", mod: "dashboard" },
  { to: "/app/virtual-office", icon: Building2, label: "Virtual Office", mod: "dashboard" },
  { to: "/app/design-dashboard", icon: PenTool, label: "Dashboard Design", mod: "workorders", alt: "hr", team: true },
  { to: "/app/production-dashboard", icon: Factory, label: "Dashboard Produksi", mod: "production", team: true },
  { to: "/app/finishing", icon: Hammer, label: "Dashboard Finishing", mod: "production", team: true },
  { to: "/app/qc", icon: ClipboardList, label: "Dashboard QC", mod: "qc", flow: "qc" },
  { to: "/app/dokumentasi", icon: Building2, label: "Social Media & Dokumentasi", mod: "docs", flow: "docs" },
  { to: "/app/packing", icon: PackageCheck, label: "Dashboard Packing", mod: "packing", flow: "packing" },
  { to: "/app/teknisi", icon: Wrench, label: "Dashboard Teknisi", mod: "dashboard", flow: "teknisi" },
  { to: "/app/pengiriman", icon: Truck, label: "Pickup & Pengiriman", mod: "shipping", flow: "shipping" },
  { group: "Sales & CRM" },
  { to: "/app/sales", icon: Target, label: "Dashboard Sales", mod: "crm", flow: "sales" },
  { to: "/app/crm", icon: Users, label: "Customers", mod: "crm" },
  { to: "/app/consignments", icon: PackageCheck, label: "Titip Barang", mod: "crm" },
  { to: "/app/catalog", icon: BookOpen, label: "Katalog Produk", mod: "quotations" },
  { to: "/app/quotations", icon: FileText, label: "Quotations", mod: "quotations" },
  { to: "/app/tasks", icon: ListTodo, label: "Tugas & Reminder", mod: "quotations", alt: "purchasing" },
  { to: "/app/invoices", icon: ReceiptText, label: "Invoice", mod: "quotations" },
  { to: "/app/receivables", icon: TrendingDown, label: "Laporan Piutang", mod: "quotations" },
  { to: "/app/bonus-sales", icon: Target, label: "Penjualan & Laporan Bonus", mod: "dashboard", always: true },
  { to: "/app/store", icon: Store, label: "Toko Offline (POS & Konsinyasi)", mod: "dashboard", always: true },
  { to: "/app/design-estimator", icon: Calculator, label: "Kalkulator Design", mod: "quotations" },
  { to: "/app/booking", icon: CalendarCheck, label: "Booking", mod: "booking" },
  { group: "Operasional" },
  { to: "/app/projects", icon: FolderKanban, label: "Projects", mod: "projects", alt: "finance" },
  { to: "/app/workorders", icon: ClipboardList, label: "Work Orders", mod: "workorders" },
  { to: "/tv/wo", icon: Monitor, label: "Papan TV Progres WO", mod: "workorders" },
  { to: "/app/production", icon: Factory, label: "Progress WO", always: true },
  { to: "/app/workflow", icon: Workflow, label: "Workflow Builder", mod: "workflow" },
  { to: "/app/delivery", icon: Truck, label: "Pengiriman", mod: "delivery" },
  { group: "Supply Chain" },
  { to: "/app/stock", icon: Boxes, label: "Stock", mod: "stock" },
  { to: "/app/assets", icon: HardDrive, label: "Aset", mod: "stock" },
  { to: "/app/purchasing", icon: PackageSearch, label: "Purchasing", mod: "purchasing" },
  { group: "HR & Finance" },
  { to: "/app/hr", icon: UserCog, label: "Employees", mod: "hr" },
  { to: "/app/attendance", icon: Clock, label: "Attendance & Ijin & Peraturan", mod: "attendance", always: true },
  { to: "/app/payroll", icon: Wallet, label: "Payroll", mod: "payroll" },
  { to: "/app/finance", icon: Landmark, label: "Finance", mod: "finance" },
  { to: "/app/petty-cash", icon: Wallet, label: "Petty Cash", mod: "finance", alt: "purchasing" },
  { to: "/app/tax", icon: ScrollText, label: "TAX", mod: "finance" },
  { to: "/app/wo-budget", icon: PiggyBank, label: "Anggaran per WO", mod: "finance" },
  { group: "System" },
  { to: "/app/settings", icon: Settings, label: "Settings", mod: "settings" },
  { to: "/app/audit", icon: ScrollText, label: "Audit Trail", mod: "audit" },
];

const FLOW_MODS = ["qc", "docs", "packing", "shipping"];

// Menu bawaan sebuah role dari akses modulnya (dipakai bila admin belum mengatur menu manual)
export const defaultMenus = (key, def) => {
  const mods = def?.modules || [], vo = def?.view_only || [];
  if (key === "viewer") return ["/app/workorders", "/tv/wo"];
  const flowOn = (i) => (FLOW_MODS.includes(i.mod) ? mods.includes(i.mod) : i.flow === "teknisi" ? key === "teknisi" : ["sales", "sales_manager"].includes(key));
  return MENU.filter((i) => !i.group && (i.always || (i.flow ? flowOn(i) : mods.includes(i.mod) && !(i.team && vo.includes(i.mod))) || (i.alt && mods.includes(i.alt) && !vo.includes(i.alt)))).map((i) => i.to);
};
export const roleMenuList = (key, def) => def?.menus || defaultMenus(key, def).filter((p) => !(def?.menus_off || []).includes(p));

export default function ErpLayout() {
  const { user, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [repairRoles, setRepairRoles] = useState([]);
  const [flowMy, setFlowMy] = useState({});
  const [taskCount, setTaskCount] = useState(0);
  const [consignCount, setConsignCount] = useState(0);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [menuOrder, setMenuOrder] = useState({ order: [], can_edit: false });
  const [orderOpen, setOrderOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const loadNotifs = () => { api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {}); };
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const curItem = MENU.filter((i) => i.to && pathname.startsWith(i.to)).sort((a, b) => b.to.length - a.to.length)[0];
  const curMod = curItem?.mod;
  const viewOnly = !!curMod && (user?.view_only || []).includes(curMod);

  useEffect(() => {
    if (user) api.get("/settings/repair-rules").then(({ data }) => setRepairRoles(data.edit_roles || [])).catch(() => {});
    if (user) api.get("/flow/my").then(({ data }) => setFlowMy(data)).catch(() => {});
    if (user) api.get("/settings/menu-order").then(({ data }) => setMenuOrder(data)).catch(() => {});
  }, [user?.role]);

  useEffect(() => {
    if (!user) return;
    const fetchCount = () => { api.get("/tasks/count").then(({ data }) => setTaskCount(data.count || 0)).catch(() => {}); api.get("/consign-requests/count").then(({ data }) => setConsignCount(data.count || 0)).catch(() => {}); };
    fetchCount();
    loadNotifs();
    const n = setInterval(loadNotifs, 60000);
    const t = setInterval(fetchCount, 120000);
    return () => { clearInterval(t); clearInterval(n); };
  }, [user?.role]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400 font-mono text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "viewer" && !pathname.startsWith("/app/workorders")) return <Navigate to="/app/workorders" replace />;

  const defs = user.role_defs || {};
  const explicit = Object.values(defs).some((d) => Array.isArray(d.menus));
  const allowed = explicit ? new Set(Object.entries(defs).flatMap(([k, d]) => roleMenuList(k, d))) : null;
  const off = user.menus_off || [];
  const vOnly = viewOnly || !!(explicit && curItem && Object.entries(defs).filter(([k, d]) => roleMenuList(k, d).includes(curItem.to)).every(([, d]) => (d.menus_view || []).includes(curItem.to)));
  if (curItem && (explicit ? !allowed.has(curItem.to) : off.includes(curItem.to)) && curItem.to !== "/app/dashboard") return <Navigate to="/app/dashboard" replace />;

  // role yang diberi wewenang edit repair tetap bisa membuka halaman Settings
  const vo = user.view_only || [];
  const flowOk = (i) => (flowMy[i.flow] || (i.flow === "sales" && hasRole(user, "sales_manager"))) && (!FLOW_MODS.includes(i.mod) || can(user, i.mod));
  // dashboard tim hanya untuk role dengan akses edit modulnya (akses lihat saja cukup lewat Work Orders / Progress WO)
  const modOk = (i) => can(user, i.mod) && !(i.team && vo.includes(i.mod));
  const items = applyOrder(MENU, menuOrder.order).filter((i) => i.group || (explicit ? allowed.has(i.to) : (i.always || (i.flow ? flowOk(i) : modOk(i)) || (i.alt && can(user, i.alt) && !vo.includes(i.alt)) || (i.mod === "settings" && hasRole(user, ...repairRoles)))))
    .filter((i) => i.group || explicit || !off.includes(i.to))
    .filter((i, k, arr) => !i.group || (arr[k + 1] && !arr[k + 1].group))
    .filter((i) => user.role !== "viewer" || i.to === "/app/workorders" || i.to === "/tv/wo");

  return (
    <div className="h-screen flex overflow-hidden bg-slate-50">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-slate-900 text-slate-300 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center justify-between px-5 border-b border-slate-800">
          <img src="/logo-fomu-white.png" alt="FOMU 3D PRINT" className="h-8 w-auto" />
          <button className="lg:hidden" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {menuOrder.can_edit && <button type="button" data-testid="menu-order-btn" onClick={() => setOrderOpen(true)} className="w-full flex items-center gap-2 px-3 py-2 mb-2 rounded-lg text-xs font-semibold text-amber-300 border border-amber-400/40 bg-amber-400/10 hover:bg-amber-400/20 transition-colors"><ArrowUpDown className="w-4 h-4" /> Atur urutan menu</button>}
          {items.map((i, idx) =>
            i.group ? (
              <div key={idx} className="px-3 pt-4 pb-1 text-[10px] font-mono uppercase tracking-wider text-slate-500">{i.group}</div>
            ) : (
              <NavLink key={i.to} to={i.to} data-testid={`nav-${i.mod}`} onClick={() => setOpen(false)}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-sky-500/15 text-sky-400" : "hover:bg-slate-800 hover:text-white"}`}>
                <i.icon className="w-4 h-4" /> <span className="flex-1">{i.label}</span>
                {i.to === "/app/tasks" && taskCount > 0 && <span data-testid="nav-tasks-badge" className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold">{taskCount > 99 ? "99+" : taskCount}</span>}
                {i.to === "/app/consignments" && consignCount > 0 && <span data-testid="nav-consign-badge" className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">{consignCount > 99 ? "99+" : consignCount}</span>}
              </NavLink>
            )
          )}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-3 px-2 py-2">
            {user.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center text-white text-sm">{user.name?.[0]}</div>}
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate">{user.name}</div>
              <div className="text-[11px] text-slate-500 font-mono uppercase">{user.role}</div>
            </div>
            <NotificationBell data={notifs} reload={loadNotifs} id="notif" />
          </div>
          {user.has_password && <button data-testid="change-password-btn" onClick={() => setPwOpen(true)} className="w-full mt-1 flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-slate-800 hover:text-white"><KeyRound className="w-4 h-4" /> Ganti Password</button>}
          <button data-testid="logout-btn" onClick={logout} className="w-full mt-1 flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-slate-800 hover:text-white"><LogOut className="w-4 h-4" /> Keluar</button>
        </div>
      </aside>

      {(user.must_change || pwOpen) && <ChangePasswordModal forced={user.must_change} onClose={() => setPwOpen(false)} />}
      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      {orderOpen && <MenuOrderModal menu={applyOrder(MENU, menuOrder.order)} close={() => setOrderOpen(false)} saved={(order) => setMenuOrder({ ...menuOrder, order })} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center gap-3 px-5 lg:hidden">
          <button onClick={() => setOpen(true)} className="relative" data-testid="mobile-menu-btn"><Menu className="w-6 h-6" />{notifs.unread > 0 && <span data-testid="mobile-notif-dot" className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-rose-500" />}</button>
          <span className="font-head font-bold">FOMU 3D PRINT</span>
        </header>
        <main className={`flex-1 overflow-y-auto p-5 sm:p-8 ${vOnly ? "view-only-mode" : ""}`}>
          {vOnly && <div data-testid="view-only-banner" className="mb-4 rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-sm text-sky-800">Mode lihat saja — Anda bisa membuka halaman ini tetapi tidak bisa menambah, mengubah, atau menghapus data.</div>}
          <ScoreStrip path={pathname} />
          <Outlet />
        </main>
        <DesignFloatBar />
        <SpecialPickModal />
        <IdleWatch />
        <ProdTimerWatcher user={user} />
      </div>
    </div>
  );
}
